import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { MeOutput, TenantSummary } from "@zapir/shared";

/* ---------------- Sessao / tenant ativo ---------------- */

interface SessionState {
  accessToken: string | null;
  me: MeOutput | null;
  tenantId: string | null;
  setLogin: (accessToken: string, me: MeOutput) => void;
  setMe: (me: MeOutput) => void;
  setTenant: (tenantId: string | null) => void;
  logout: () => void;
}

export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      accessToken: null,
      me: null,
      tenantId: null,
      setLogin: (accessToken, me) => set({ accessToken, me }),
      setMe: (me) => set({ me }),
      setTenant: (tenantId) => set({ tenantId }),
      logout: () => set({ accessToken: null, me: null, tenantId: null }),
    }),
    { name: "zapir.session" },
  ),
);

export function activeTenant(): TenantSummary | null {
  const { me, tenantId } = useSession.getState();
  if (!me) return null;
  return me.tenants.find((t) => t.id === tenantId) ?? null;
}

/* ---------------- Preferencias locais do dispositivo ---------------- */

export type Theme = "light" | "dark" | "system";
export type Density = "comfortable" | "compact";

interface PrefsState {
  theme: Theme;
  density: Density;
  dataSaver: boolean;
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
  setDataSaver: (dataSaver: boolean) => void;
}

export const usePrefs = create<PrefsState>()(
  persist(
    (set) => ({
      theme: "system",
      density: "comfortable",
      dataSaver: false,
      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      setDataSaver: (dataSaver) => set({ dataSaver }),
    }),
    { name: "zapir.prefs" },
  ),
);

/* ---------------- Conectividade (nao persistida) ---------------- */

export type ConnState = "online" | "reconnecting" | "offline";

interface ConnectionStore {
  conn: ConnState;
  pendingCount: number;
  setConn: (conn: ConnState) => void;
  setPendingCount: (n: number) => void;
}

export const useConnection = create<ConnectionStore>((set) => ({
  conn: "online",
  pendingCount: 0,
  setConn: (conn) => set({ conn }),
  setPendingCount: (pendingCount) => set({ pendingCount }),
}));

export const connLabel: Record<ConnState, string> = {
  online: "Online",
  reconnecting: "Reconectando…",
  offline: "Sem conexão",
};

/* ---------------- Feedback transiente (toast com retry/undo) ---------------- */

export interface Toast {
  id: number;
  tone: "info" | "success" | "danger";
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastStore {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => number;
  dismiss: (id: number) => void;
}

let toastSeq = 0;

export const useToasts = create<ToastStore>((set) => ({
  toasts: [],
  push: (toast) => {
    const id = ++toastSeq;
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }));
    window.setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 5000);
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function toast(t: Omit<Toast, "id">): void {
  useToasts.getState().push(t);
}
